/**
 * API client for the Operations dashboards.
 *
 * Mirrors the standalone dashboard's api/{catalogMapping,processMapping,
 * distroProduct,operationsUpdates}.js surface, but targets this backend's
 * /api/operations/* routes and sends the JWT the rest of the app uses.
 *
 * Route mapping vs the reference API:
 *   /api/operations/catalog               -> same
 *   /api/roster/distro-product            -> /api/operations/commitments
 *   /api/operations/process               -> same
 *   /api/operations/updates               -> same
 *
 * Assets and Creative read the same box_assets crawl snapshot from Postgres,
 * so they need no Box credential. Only the live refresh crawl, release mail and
 * live folder listing do -- those are not ported. Waiver (Outlook/Graph) is not
 * ported either.
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

export const OPERATIONS_LIST_LIMIT = 5000;

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchJson(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(options.body && !(options.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      // This backend returns {error}; the reference API returned {detail}.
      detail = body.error ?? body.detail ?? detail;
    } catch {
      // ignore parse errors
    }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  if (res.status === 204) return null;
  return res.json();
}

/** Status / product-type vocabularies, so the UI has one source of truth. */
export function fetchOperationsOptions() {
  return fetchJson('/api/operations/options');
}

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

/**
 * Catalog rows.
 * `mapped`: 'mapped' (default view) hides taken-down products; 'all'
 * ("Show All") adds them plus unmapped Assets folders as placeholders;
 * 'unmapped' returns only those placeholders.
 * `section`: All | Singles | Albums | Videos | Distribution | Physical.
 */
export function fetchCatalog({
  q,
  mapped = 'mapped',
  section = 'All',
  limit = OPERATIONS_LIST_LIMIT,
  offset,
} = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (mapped) params.set('mapped', mapped);
  if (section) params.set('section', section);
  if (limit != null) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  const qs = params.toString();
  return fetchJson(`/api/operations/catalog${qs ? `?${qs}` : ''}`);
}

export function createCatalogRow(body) {
  return fetchJson('/api/operations/catalog', {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

/** Only the keys present in `body` are written, so a partial edit is safe. */
export function updateCatalogRow(encoreId, body) {
  return fetchJson(`/api/operations/catalog/${encodeURIComponent(encoreId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body || {}),
  });
}

export function deleteCatalogRow(encoreId) {
  return fetchJson(`/api/operations/catalog/${encodeURIComponent(encoreId)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Process
// ---------------------------------------------------------------------------

export function fetchProcess({ q, status, limit = OPERATIONS_LIST_LIMIT, offset } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (status) params.set('status', status);
  if (limit != null) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  const qs = params.toString();
  return fetchJson(`/api/operations/process${qs ? `?${qs}` : ''}`);
}

export function createProcessRow(body) {
  return fetchJson('/api/operations/process', {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

export function updateProcessRow(rowId, body) {
  return fetchJson(`/api/operations/process/${Number(rowId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body || {}),
  });
}

export function deleteProcessRow(rowId) {
  return fetchJson(`/api/operations/process/${Number(rowId)}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Commitments
// ---------------------------------------------------------------------------

export function fetchCommitments({
  q,
  includeHidden = false,
  limit = OPERATIONS_LIST_LIMIT,
  offset,
} = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (includeHidden) params.set('include_hidden', '1');
  if (limit != null) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  const qs = params.toString();
  return fetchJson(`/api/operations/commitments${qs ? `?${qs}` : ''}`);
}

/** Upsert one artist's commitment row; it is created on first edit. */
export function updateCommitment(artistMappingId, body) {
  return fetchJson(`/api/operations/commitments/${Number(artistMappingId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body || {}),
  });
}

// ---------------------------------------------------------------------------
// Updates (read-only audit feed)
// ---------------------------------------------------------------------------

export function fetchOperationsUpdates({ q, dashboard, limit = 500, offset } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (dashboard) params.set('dashboard', dashboard);
  if (limit != null) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  const qs = params.toString();
  return fetchJson(`/api/operations/updates${qs ? `?${qs}` : ''}`);
}

// ---------------------------------------------------------------------------
// Assets / Creative (Box crawl snapshot; read needs no Box credential)
// ---------------------------------------------------------------------------

function assetsQuery({ q, status, limit = OPERATIONS_LIST_LIMIT, offset } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (status) params.set('status', status);
  if (limit != null) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  return params.toString();
}

export function fetchAssets(opts = {}) {
  const qs = assetsQuery(opts);
  return fetchJson(`/api/operations/assets${qs ? `?${qs}` : ''}`);
}

export function fetchCreative(opts = {}) {
  const qs = assetsQuery(opts);
  return fetchJson(`/api/operations/creative${qs ? `?${qs}` : ''}`);
}

/** Mark a release family done so the external crawl skips it. */
export function setReleaseLock({ releaseFolderId, locked = true } = {}) {
  return fetchJson('/api/operations/assets/lock', {
    method: 'POST',
    body: JSON.stringify({ release_folder_id: releaseFolderId, locked }),
  });
}

/** One track's credits, for the Assets expand panel. */
export function fetchAssetsSongCredits(trackFolderId) {
  const params = new URLSearchParams();
  params.set('track_folder_id', String(trackFolderId || ''));
  return fetchJson(`/api/operations/assets/song-credits?${params}`);
}

/** Batch credits for several tracks at once (Expand all). */
export function fetchAssetsSongCreditsBatch(trackFolderIds) {
  return fetchJson('/api/operations/assets/song-credits/batch', {
    method: 'POST',
    body: JSON.stringify({ track_folder_ids: trackFolderIds || [] }),
  });
}

export function fetchAssetsCredits({ trackFolderId, releaseFolderId } = {}) {
  const params = new URLSearchParams();
  if (trackFolderId) params.set('track_folder_id', trackFolderId);
  if (releaseFolderId) params.set('release_folder_id', releaseFolderId);
  return fetchJson(`/api/operations/assets/credits?${params}`);
}

export function createAssetsCredit(body) {
  return fetchJson('/api/operations/assets/credits', {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

export function updateAssetsCredit(creditId, body) {
  return fetchJson(`/api/operations/assets/credits/${Number(creditId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body || {}),
  });
}

export function deleteAssetsCredit(creditId) {
  return fetchJson(`/api/operations/assets/credits/${Number(creditId)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Commitments -> Mapping view, manual releases, export
// ---------------------------------------------------------------------------

/** Song-level mapping: what counts toward each commitment, and why. */
export function fetchCommitmentReleases({
  q,
  includeHidden = false,
  limit = OPERATIONS_LIST_LIMIT,
  offset,
} = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (includeHidden) params.set('include_hidden', '1');
  if (limit != null) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  const qs = params.toString();
  return fetchJson(`/api/operations/commitments/releases${qs ? `?${qs}` : ''}`);
}

/**
 * Exclude / force-include a song, or override its release date.
 * Each field writes only when its `update*` flag is set, so clearing one
 * does not wipe the other.
 */
export function updateReleaseOverride({
  artistMappingId,
  songId,
  action,
  releaseDateOverride,
  updateAction = false,
  updateReleaseDateOverride = false,
} = {}) {
  return fetchJson('/api/operations/commitments/releases', {
    method: 'PATCH',
    body: JSON.stringify({
      artist_mapping_id: artistMappingId,
      song_id: songId,
      action: action ?? null,
      release_date_override: releaseDateOverride ?? null,
      update_action: Boolean(updateAction),
      update_release_date_override: Boolean(updateReleaseDateOverride),
    }),
  });
}

/** Distro artist id + name, for the manual-release picker. */
export function fetchDistroArtists({ includeHidden = false } = {}) {
  const params = new URLSearchParams();
  if (includeHidden) params.set('include_hidden', '1');
  const qs = params.toString();
  return fetchJson(`/api/operations/commitments/artists${qs ? `?${qs}` : ''}`);
}

export function createManualRelease(body) {
  return fetchJson('/api/operations/commitments/manual-releases', {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

export function updateManualRelease(manualId, body) {
  return fetchJson(`/api/operations/commitments/manual-releases/${Number(manualId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body || {}),
  });
}

export function deleteManualRelease(manualId) {
  return fetchJson(`/api/operations/commitments/manual-releases/${Number(manualId)}`, {
    method: 'DELETE',
  });
}

/** Download the current Commitments or Mapping view as CSV. */
export async function downloadCommitmentsCsv({
  view = 'commitments',
  includeHidden = false,
} = {}) {
  const params = new URLSearchParams();
  params.set('view', view);
  if (includeHidden) params.set('include_hidden', '1');
  const res = await fetch(
    `${API_BASE}/api/operations/commitments/export?${params}`,
    { headers: authHeaders() },
  );
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.error ?? body.detail ?? detail;
    } catch {
      // ignore parse errors
    }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = /filename="([^"]+)"/i.exec(disposition);
  const filename = match?.[1] || 'distro_commitments.csv';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return { filename };
}

// ---------------------------------------------------------------------------
// Waiver (Outlook drafts)
// ---------------------------------------------------------------------------

/** MSAL + Box attachment availability. No secrets. */
export function fetchWaiverOutlookConfig() {
  return fetchJson('/api/operations/waiver/outlook-config');
}

/**
 * Create an Outlook draft in the caller's own mailbox.
 *
 * The Microsoft Graph token rides in X-Graph-Token so the normal app JWT can
 * stay in Authorization and still gate the route.
 */
export function createWaiverOutlookDraft(graphToken, payload = {}) {
  const token = String(graphToken || '').trim();
  if (!token) throw new Error('Microsoft access token is required');
  return fetchJson('/api/operations/waiver/outlook-draft', {
    method: 'POST',
    headers: { 'X-Graph-Token': token },
    body: JSON.stringify({
      to: payload.to || null,
      subject: payload.subject || '',
      body: payload.body || '',
      waiver_type: payload.waiverType || payload.waiver_type || null,
      attach_payment_docs: Boolean(
        payload.attachPaymentDocs ?? payload.attach_payment_docs,
      ),
    }),
  });
}
